import { useQuery } from '@tanstack/react-query';
import { formatDateTime } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { attendanceApi } from '@/services/attendance.api';
import { EmptyState, Spinner } from '@/components/ui';

/**
 * The reference's "Actividades" tab, built for real on top of Attendance —
 * the only per-person activity source that already supports a `personId`
 * filter end to end. Events registrations don't (see the redesign plan),
 * so this stays attendance-only rather than a wider synthetic feed.
 */
export function ActivitiesTab({ personId }: { readonly personId: string }) {
  const { t, locale } = useI18n();

  const query = useQuery({
    queryKey: ['attendance', { personId }],
    queryFn: ({ signal }) => attendanceApi.list({ personId, limit: 25 }, signal),
  });

  if (query.isPending) return <Spinner label={t('common.states.loading')} />;

  const records = query.data?.data ?? [];

  if (records.length === 0) {
    return <EmptyState title={t('attendance.record.empty')} />;
  }

  return (
    <dl className="detail-grid">
      {records.map((record) => (
        <div key={record.id} className="detail-row">
          <dt className="detail-label">
            {record.contextLabel ?? t(`attendance.contextType.${record.contextType}`)}
          </dt>
          <dd className="detail-value" dir="ltr">
            {formatDateTime(record.checkedInAt, locale)}
            {record.checkedOutAt && ` – ${formatDateTime(record.checkedOutAt, locale)}`}
          </dd>
        </div>
      ))}
    </dl>
  );
}
